import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Textarea } from './ui/textarea';
import { ArrowLeft, Send, Sparkles, Trophy, CheckCircle2, Star, TrendingUp } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { Progress } from './ui/progress';
import axios from 'axios';
import LoadingAnimation from './LoadingAnimation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GIRAFFE_MASCOT = '/images/simby-mascote.png';

const Simulator = () => {
  const { lessonId } = useParams(); // lessonId is actually a module_id when coming from LessonModules
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const [scenario, setScenario] = useState(null);
  const [moduleData, setModuleData] = useState(null); // holds lesson_id for back navigation
  const [islandId, setIslandId] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [userMessageCount, setUserMessageCount] = useState(0);
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [isEvaluating, setIsEvaluating] = useState(false);

  useEffect(() => {
    const fetchScenario = async () => {
      try {
        // First try to fetch as a module (new flow: lessonId is actually a module_id)
        let parentLessonId = null;
        try {
          const modRes = await axios.get(`${API}/modules/${lessonId}`, { withCredentials: true });
          setModuleData(modRes.data);
          parentLessonId = modRes.data.lesson_id;
          // Fetch parent lesson to get island_id (same pattern as VideoLesson/QuizLesson)
          if (modRes.data.lesson_id) {
            try {
              const lessonRes = await axios.get(`${API}/lessons/${modRes.data.lesson_id}`, { withCredentials: true });
              setIslandId(lessonRes.data.island_id || 1);
            } catch { /* mantém padrão 1 */ }
          }
        } catch (_) {
          // Not a module — could be a direct scenario_id (old flow)
        }

        // Fetch scenario: backend resolves module_id → scenario_id automatically
        const response = await axios.get(`${API}/simulators/${lessonId}`, { withCredentials: true });
        const scenarioData = response.data;
        setScenario(scenarioData);

        setMessages([
          {
            role: 'system',
            content: `**Cenário:** ${scenarioData.situation || scenarioData.description}`,
            timestamp: new Date()
          },
          {
            role: 'assistant',
            content: scenarioData.initial_message || scenarioData.description || 'Olá! Vamos começar a simulação.',
            timestamp: new Date()
          }
        ]);
      } catch (error) {
        console.error('Error fetching scenario:', error);
        setScenario(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchScenario();
  }, [lessonId]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-50 to-white">
        <div className="text-center">
          <LoadingAnimation size="md" className="mx-auto mb-4" />
          <p className="text-gray-600">Carregando simulador...</p>
        </div>
      </div>
    );
  }

  if (!scenario) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <p className="text-xl text-gray-600">Cenário não encontrado</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Voltar ao Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  // Call AI API for response
  const getAIResponse = async (userMessage, allMessages) => {
    try {
      const response = await axios.post(
        `${API}/simulator/chat`,
        {
          scenario_id: lessonId,
          messages: allMessages.map(m => ({ role: m.role, content: m.content })),
          user_message: userMessage
        },
        { withCredentials: true }
      );
      return response.data.response;
    } catch (error) {
      console.error('AI response error:', error);
      // Fallback message if API fails
      return 'Entendo o que você está dizendo... continue.';
    }
  };

  // Call AI API for evaluation
  const evaluateConversation = async (allMessages) => {
    try {
      setIsEvaluating(true);
      const response = await axios.post(
        `${API}/simulator/evaluate`,
        {
          scenario_id: lessonId,
          messages: allMessages.map(m => ({ role: m.role, content: m.content }))
        },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Evaluation error:', error);
      // Fallback evaluation if API fails
      return {
        totalScore: 60,
        maxScore: 100,
        criteriaScores: {},
        feedback: 'Bom trabalho na prática da Inteligência Relacional! Continue desenvolvendo suas habilidades.',
        strengths: ['Participação ativa'],
        improvements: ['Continue praticando os 4 componentes da Inteligência Relacional'],
        xpEarned: 100
      };
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || showEvaluation || isTyping) return;

    const newMessageCount = userMessageCount + 1;

    // Add user message
    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setUserMessageCount(newMessageCount);
    setIsTyping(true);

    // Check if this is the last message
    const maxMsgs = scenario.max_messages || scenario.maxMessages || 6;
    if (newMessageCount >= maxMsgs) {
      // Get final AI response
      const aiResponse = await getAIResponse(input, updatedMessages);
      const finalMessages = [...updatedMessages, {
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date()
      }];
      setMessages(finalMessages);
      setIsTyping(false);

      // Evaluate the conversation
      const evalResult = await evaluateConversation(finalMessages);
      setEvaluation(evalResult);

      // Save to progress tracking
      try {
        await axios.post(
          `${API}/progress/simulator`,
          {
            scenario_id: lessonId,
            total_score: evalResult.totalScore || 0,
            criteria_scores: evalResult.criteriaScores || {},
            strengths: evalResult.strengths || [],
            improvements: evalResult.improvements || [],
            feedback: evalResult.feedback || '',
            xp_earned: evalResult.xpEarned || 0
          },
          { withCredentials: true }
        );
      } catch (progressError) {
        console.error('Error saving simulator progress:', progressError);
      }

      // Mark module as complete and award XP
      try {
        await axios.post(
          `${API}/auth/complete-lesson`,
          {
            lesson_id: lessonId, // module_id in the new flow
            action_type: 'module',
            island_id: islandId,
            score: evalResult.totalScore || 0
          },
          { withCredentials: true }
        );
        await checkAuth();
      } catch (completionError) {
        console.error('Error marking simulator complete:', completionError);
      }

      setShowEvaluation(true);
    } else {
      // Get AI response
      const aiResponse = await getAIResponse(input, updatedMessages);
      setMessages([...updatedMessages, {
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date()
      }]);
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (isEvaluating) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <LoadingAnimation size="md" className="mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">🦒 A Simby está analisando...</h2>
          <p className="text-gray-600">
            Avaliando sua prática de <strong>Comunicação Não-Violenta</strong>:
            observação, sentimentos, necessidades e pedidos.
          </p>
        </Card>
      </div>
    );
  }

  if (showEvaluation && evaluation) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-3xl w-full p-8">
          {/* Simby Header */}
          <div className="text-center mb-6">
            <img src={GIRAFFE_MASCOT} alt="Simby" className="w-32 h-32 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-purple-600 mb-2">
              {evaluation.simbyGreeting || 'Olá! 🦒'}
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed max-w-xl mx-auto">
              {evaluation.simbyMessage || 'Vamos ver como foi sua prática de Inteligência Relacional!'}
            </p>
          </div>

          {/* Score Total */}
          <div className="bg-gradient-to-r from-purple-600 to-purple-400 rounded-2xl p-6 text-white mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm opacity-90">Pontuação Total</div>
                <div className="text-5xl font-bold">{evaluation.totalScore}</div>
              </div>
              <Trophy className="w-16 h-16 opacity-80" />
            </div>
            <Progress
              value={evaluation.totalScore}
              className="h-3 bg-purple-300"
            />
            <div className="mt-4 text-sm">
              <div className="font-semibold mb-1">XP Ganho: +{evaluation.xpEarned} XP 🎉</div>
            </div>
          </div>

          {/* Simby Tip */}
          {evaluation.simbyTip && (
            <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <span className="text-2xl">💡</span>
                <div>
                  <p className="font-semibold text-purple-800 mb-1">Dica da Simby</p>
                  <p className="text-purple-700">{evaluation.simbyTip}</p>
                </div>
              </div>
            </div>
          )}

          {/* Strengths and Improvements */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {/* Strengths */}
            {evaluation.strengths && evaluation.strengths.length > 0 && (
              <Card className="p-4 bg-green-50 border-2 border-green-200">
                <h3 className="font-bold text-green-800 mb-3 flex items-center">
                  <Star className="w-5 h-5 mr-2" />
                  Pontos Fortes
                </h3>
                <ul className="space-y-2 text-sm text-green-700">
                  {evaluation.strengths.map((s, i) => (
                    <li key={i} className="flex items-start">
                      <CheckCircle2 className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Improvements */}
            {evaluation.improvements && evaluation.improvements.length > 0 && (
              <Card className="p-4 bg-yellow-50 border-2 border-yellow-200">
                <h3 className="font-bold text-yellow-800 mb-3 flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2" />
                  Sugestões de Melhoria
                </h3>
                <ul className="space-y-2 text-sm text-yellow-700">
                  {evaluation.improvements.map((imp, i) => (
                    <li key={i} className="flex items-start">
                      <span className="text-yellow-600 mr-2">•</span>
                      <span>{imp}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>

          {/* Criteria Scores */}
          {evaluation.criteriaScores && Object.keys(evaluation.criteriaScores).length > 0 && (
            <Card className="p-6 mb-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
                Análise por Critério
              </h3>
              <div className="space-y-3">
                {Object.entries(evaluation.criteriaScores).map(([criterion, score], index) => {
                  const achieved = score >= 15;

                  return (
                    <div key={index} className="flex items-start space-x-3">
                      <div className={`mt-0.5 ${achieved ? 'text-green-500' : 'text-gray-400'}`}>
                        <CheckCircle2 className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm ${achieved ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                          {criterion}
                        </p>
                        <div className="mt-1">
                          <Progress value={(score / 20) * 100} className="h-1" />
                        </div>
                      </div>
                      <span className={`text-sm font-semibold ${achieved ? 'text-green-600' : 'text-gray-400'}`}>
                        {score}/20
                      </span>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Encouragement */}
          {evaluation.encouragement && (
            <div className="text-center mb-6">
              <p className="text-lg text-gray-700 italic">
                "{evaluation.encouragement}" 🦒
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-4">
            {moduleData?.lesson_id ? (
              <Button
                onClick={() => navigate(`/lesson-modules/${moduleData.lesson_id}`)}
                size="lg"
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                Voltar à Lição
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/dashboard')}
                size="lg"
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                Voltar ao Dashboard
              </Button>
            )}
            <Button
              onClick={() => window.location.reload()}
              size="lg"
              variant="outline"
              className="flex-1 border-2 border-purple-600 text-purple-600"
            >
              Tentar Novamente
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            onClick={() => moduleData?.lesson_id
              ? navigate(`/lesson-modules/${moduleData.lesson_id}`)
              : navigate('/dashboard')}
            variant="ghost"
            size="sm"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 mb-1">{scenario.title}</h1>
              <p className="text-gray-600">{scenario.description}</p>
              <div className="mt-3 flex items-center flex-wrap gap-2 text-sm">
                <div className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-medium flex items-center gap-1">
                  <span>👤 Você:</span> {scenario.user_role || scenario.userRole || 'Praticante de Inteligência Relacional'}
                </div>
                <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium flex items-center gap-1">
                  <span>{scenario.character || '🎭'}</span> {scenario.ai_role || scenario.aiRole || 'Personagem'}
                </div>
              </div>
            </div>
            <div className="text-6xl">{scenario.character}</div>
          </div>

          {/* Progress */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">
                Mensagens: {userMessageCount}/{scenario.max_messages || scenario.maxMessages || 6}
              </span>
              <span className="text-sm text-gray-500">
                {(scenario.max_messages || scenario.maxMessages || 6) - userMessageCount} restantes
              </span>
            </div>
            <Progress value={(userMessageCount / (scenario.max_messages || scenario.maxMessages || 6)) * 100} className="h-2" />
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Chat Area */}
          <Card className="lg:col-span-2 flex flex-col" style={{ height: '600px' }}>
            {/* Messages */}
            <ScrollArea className="flex-1 p-6">
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'
                      }`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${message.role === 'system'
                        ? 'bg-purple-100 text-purple-900 w-full text-center font-semibold'
                        : message.role === 'user'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-900'
                        }`}
                    >
                      <p className="whitespace-pre-wrap">{message.content}</p>
                      <p className="text-xs mt-1 opacity-70">
                        {message.timestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}

                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-2xl px-4 py-3">
                      <div className="flex space-x-2">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="border-t border-gray-200 p-4">
              <div className="flex space-x-2">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Digite sua resposta usando Inteligência Relacional..."
                  className="resize-none"
                  rows={3}
                  disabled={userMessageCount >= (scenario.max_messages || scenario.maxMessages || 6) || isTyping}
                />
                <Button
                  onClick={handleSend}
                  className="bg-purple-600 hover:bg-purple-700"
                  disabled={!input.trim() || userMessageCount >= (scenario.max_messages || scenario.maxMessages || 6) || isTyping}
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              {userMessageCount >= (scenario.max_messages || scenario.maxMessages || 6) && (
                <p className="text-sm text-gray-600 mt-2 text-center">
                  Aguarde a avaliação da conversa...
                </p>
              )}
            </div>
          </Card>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Tips Card */}
            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Dicas para Inteligência Relacional</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">•</span>
                  <span>Comece com uma observação neutra</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">•</span>
                  <span>Expresse seus sentimentos</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">•</span>
                  <span>Identifique suas necessidades</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">•</span>
                  <span>Faça um pedido claro e específico</span>
                </li>
              </ul>
            </Card>

            {/* AI Badge */}
            <Card className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-purple-200">
              <div className="flex items-center space-x-2 text-purple-700">
                <Sparkles className="w-5 h-5" />
                <span className="font-medium text-sm">Powered by Gemini AI</span>
              </div>
              <p className="text-xs text-gray-600 mt-1">
                Respostas inteligentes e avaliação personalizada
              </p>
            </Card>

            {/* Mascot Encouragement */}
            <Card className="p-6 bg-gradient-to-r from-purple-100 to-purple-50">
              <div className="text-center">
                <img src={GIRAFFE_MASCOT} alt="Simby" className="w-24 h-24 mx-auto mb-2" />
                <p className="text-sm text-gray-700">
                  Continue praticando! Cada conversa é uma oportunidade de crescimento.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Simulator;

